import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth, LoginPage } from './features/auth';
import { authService } from './shared/services/auth.service';
import { Header } from './shared/components/layout';
import { DialogProvider } from './shared/components/DialogProvider';
import { DeadlineDashboard } from './features/deadlines';
import { MorningDeadlineModal } from './features/deadlines/components/MorningDeadlineModal';
import { CustomerMasterDataDashboard } from './features/customers/components/CustomerMasterDataDashboard';
import { KommunikationDashboard } from './features/kommunikation';
import { InvoiceDashboard } from './features/invoices';
import { RevenueAnalyticsDashboard } from './features/invoices/components/RevenueAnalyticsDashboard';
import { DatevWeiterberechnungPage } from './features/datev-weiterberechnung';
import { UrlaubsplanerDashboard } from './features/urlaubsplaner';
import { TicketsPage, TicketDetail } from './features/tickets';
import { PosteingangPage } from './features/posteingang';
import { BeitragsrechnungPage } from './features/beitragsrechnung';
import { DatevCallback } from './pages/DatevCallback';
import { DatevDashboard } from './pages/DatevDashboard';
import { DocumentAccessPage } from './pages/DocumentAccessPage';
import { SecureDocumentAccessPage } from './pages/SecureDocumentAccessPage';
import { SepaMandatPage } from './pages/SepaMandatPage';
import { SetPasswordPage } from './pages/SetPasswordPage';
import { ChatZustimmungPage } from './pages/ChatZustimmungPage';
import { OeffentlicheSeite } from './shared/components/OeffentlicheSeite';
import { VersionCheck } from './components/VersionCheck';

const App: React.FC = () => {
  const { user, loading, handleGoogleLogin, handleSignOut } = useAuth();
  const [supabaseReady, setSupabaseReady] = useState(false);
  const [supabaseError, setSupabaseError] = useState(false);

  useEffect(() => {
    if (user) {
      const initializeSupabaseAuth = async () => {
        try {
          authService.setupAuthListener();
          const success = await authService.autoLogin(user?.email);
          
          if (!success) {
            console.error('Supabase Authentifizierung fehlgeschlagen. Bitte prüfen Sie die Umgebungsvariablen.');
            setSupabaseError(true);
          } else {
            setSupabaseReady(true);
          }
        } catch (error) {
          console.error('Supabase auth initialization error:', error);
          setSupabaseError(true);
        }
      };

      initializeSupabaseAuth();
    }
  }, [user]);

  const handleExtendedSignOut = async () => {
    handleSignOut();
    setSupabaseReady(false);
    setSupabaseError(false);
  };

  return (
    <DialogProvider>
      <Router>
        <Routes>
          {/* PUBLIC ROUTES - Keine Authentifizierung erforderlich */}
          <Route path="/dokument/:token" element={<OeffentlicheSeite anwendung="Gehaltsunterlagen"><DocumentAccessPage /></OeffentlicheSeite>} />
          <Route path="/dokumente/:token" element={<OeffentlicheSeite anwendung="Dokumentenbereich" loeschhinweis><SecureDocumentAccessPage /></OeffentlicheSeite>} />
          <Route path="/sepa-mandat/:token" element={<OeffentlicheSeite anwendung="SEPA-Lastschrift"><SepaMandatPage /></OeffentlicheSeite>} />
          <Route path="/passwort-setzen/:setupToken" element={<OeffentlicheSeite anwendung="Dokumentenbereich"><SetPasswordPage /></OeffentlicheSeite>} />
          {/* Zustimmung zum Chat-Connector XTAX DATEV (eigene Anmeldung, siehe ChatZustimmungPage) */}
          <Route path="/oauth/consent" element={<OeffentlicheSeite anwendung="XTAX DATEV für Claude"><ChatZustimmungPage /></OeffentlicheSeite>} />

          {/* AUTHENTICATED ROUTES */}
          <Route
            path="/*"
            element={
              !user ? (
                <LoginPage onLogin={handleGoogleLogin} loading={loading} />
              ) : !supabaseReady && !supabaseError ? (
                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100vh',
                  fontFamily: 'inherit'
                }}>
                  <div>Verbindung zu Datenbank wird hergestellt...</div>
                </div>
              ) : supabaseError ? (
                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100vh',
                  fontFamily: 'inherit',
                  textAlign: 'center',
                  padding: '20px'
                }}>
                  <div>
                    <h1 style={{color: '#EF4444'}}>Datenbankverbindung fehlgeschlagen</h1>
                    <p>Bitte kontaktieren Sie den Administrator.</p>
                    <button
                      onClick={() => window.location.reload()}
                      style={{
                        marginTop: '20px',
                        padding: '8px 16px',
                        backgroundColor: '#3B82F6',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer'
                      }}
                    >
                      Erneut versuchen
                    </button>
                  </div>
                </div>
              ) : (
                <div style={{fontFamily: 'inherit', margin: '0', padding: '20px'}}>
                  <Header user={user} onSignOut={handleExtendedSignOut} />
                  <VersionCheck />
                  <MorningDeadlineModal userEmail={user?.email || ''} />
                  <Routes>
                    <Route path="/" element={<DeadlineDashboard />} />
                    <Route path="/kundenstammdaten" element={<CustomerMasterDataDashboard />} />
                    <Route path="/tickets" element={<TicketsPage />} />
                    <Route path="/tickets/:ticketId" element={<TicketDetail />} />
                    <Route path="/kommunikation" element={<KommunikationDashboard />} />
                    <Route path="/rechnungen" element={<InvoiceDashboard />} />
                    <Route path="/rechnungen/datev-weiterberechnung" element={<DatevWeiterberechnungPage />} />
                    <Route path="/umsatzanalyse" element={<RevenueAnalyticsDashboard />} />
                    <Route path="/urlaubsplaner" element={<UrlaubsplanerDashboard user={user} />} />
                    <Route path="/datev" element={<DatevDashboard />} />
                    <Route path="/datev/callback" element={<DatevCallback />} />
                    <Route path="/posteingang" element={<PosteingangPage />} />
                    <Route path="/beitragsrechnung" element={<BeitragsrechnungPage />} />
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                </div>
              )
            }
          />
        </Routes>
      </Router>
    </DialogProvider>
  );
};

export default App;
